import { createContext, useContext } from 'react';

import type { TenantMembership } from './actingTenant';
import type { EmgAuthClaims } from './authClient';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous' | 'no-access';

export interface EditorAuthState {
    status: AuthStatus;
    claims: EmgAuthClaims | null;
    memberships: TenantMembership[];
    actingTenantId: string | null;
    /** Effective permission codes in the acting tenant (empty when there is none). */
    permissions: ReadonlySet<string>;
    /** Fail-closed capability check: unknown code, or no acting tenant, means `false`. */
    can: (code: string) => boolean;
    selectTenant: (tenantId: string) => void;
    login: () => void;
    logout: () => void;
}

/**
 * EAA-1694: default is deliberately the fail-closed state, not `undefined`.
 *
 * A component that renders outside the provider (an embed route, a test harness) then
 * behaves as "not signed in, can do nothing" instead of throwing — which is the safe
 * reading for a permission check, and keeps the unauthenticated embed/player routes
 * working without wrapping them in an auth provider they have no use for.
 */
const FAIL_CLOSED: EditorAuthState = {
    status: 'anonymous',
    claims: null,
    memberships: [],
    actingTenantId: null,
    permissions: new Set<string>(),
    can: () => false,
    selectTenant: () => undefined,
    login: () => undefined,
    logout: () => undefined,
};

export const AuthContext = createContext<EditorAuthState>(FAIL_CLOSED);

export function useAuth(): EditorAuthState {
    return useContext(AuthContext);
}
